/**
 * Light / Dark / System (Jeremy, 4 Nov 2026; punch list S13).
 *
 * ONE setting per person, kept on the account by CANOPY and shared by both applications
 * and every computer. This app asks for it through its own API (GET /api/me/theme), which
 * asks Canopy, and saves every change the same way. When Canopy cannot be reached the API
 * says so (`available: false`) and the page shows Light, as Jeremy decided, without
 * forgetting what this browser remembered.
 *
 * localStorage `canopy.theme` is ONLY for the first paint: index.html reads it before the
 * app loads, by the same rules as `resolveTheme` (shared/src/theme.ts). The account is the
 * truth. The colours are one block in index.css, `:root[data-theme="dark"]`; this file only
 * decides which `data-theme` <html> carries.
 */
import { DEFAULT_THEME, THEME_STORAGE_KEY, isThemeChoice, parseStoredTheme, resolveTheme, type ThemeChoice } from "@scheduler/shared";
import { useCallback, useEffect, useState } from "react";

import { useSchedulerApi } from "./api/scheduler";

function systemQuery(): MediaQueryList | null {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)");
  } catch {
    return null;
  }
}

function readStored(): ThemeChoice {
  try {
    return parseStoredTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

function remember(choice: ThemeChoice): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Blocked site data: the choice still shows; the next load flashes Light until the API answers.
  }
}

function applyTheme(choice: ThemeChoice): void {
  document.documentElement.dataset.theme = resolveTheme(choice, systemQuery()?.matches ?? false);
}

/**
 * The person's choice and a setter that shows it at once, remembers it here and saves it on
 * the account. While the choice is System, a change in the computer's setting is followed live.
 */
export function useTheme(): [ThemeChoice, (next: ThemeChoice) => void] {
  const api = useSchedulerApi();
  const [choice, setChoice] = useState<ThemeChoice>(readStored);

  useEffect(() => {
    let current = true;
    api.myTheme().then(
      ({ theme, available }) => {
        if (!current || !isThemeChoice(theme)) return;
        setChoice(theme);
        // Canopy's Light-because-unreachable is shown, not remembered over a real choice.
        if (available) remember(theme);
      },
      () => {
        // Our own API could not be read: keep what this browser remembered.
      },
    );
    return () => {
      current = false;
    };
  }, [api]);

  useEffect(() => {
    applyTheme(choice);
    if (choice !== "system") return;
    const query = systemQuery();
    if (!query) return;
    const follow = () => applyTheme("system");
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, [choice]);

  const choose = useCallback(
    (next: ThemeChoice) => {
      setChoice(next);
      remember(next);
      api.setMyTheme(next).catch(() => {
        // Not saved on the account (Canopy unreachable): shown and remembered here.
      });
    },
    [api],
  );

  return [choice, choose];
}
